/**
 * Rope Renderer — PixiJS Graphics rendering for the grapple Verlet rope.
 * Draws smooth curve through particle positions with glow layer.
 */

import { Container, Graphics } from 'pixi.js';
import { Z, GRAPPLE_FX } from '../constants';
import type { ActiveGrapple, GrappleHook, RopeParticle } from '../types';

export class RopeRenderer {
  private graphics: Graphics;
  private glowGraphics: Graphics;

  // Fade state for rope release visual
  private fadeAlpha: number = 0;
  private lastParticles: { x: number; y: number }[] = [];
  private lastColor: number = 0xffffff;
  private wasActive: boolean = false;

  constructor(worldContainer: Container) {
    this.glowGraphics = new Graphics();
    this.glowGraphics.zIndex = Z.PARTICLES;
    worldContainer.addChild(this.glowGraphics);

    this.graphics = new Graphics();
    this.graphics.zIndex = Z.PARTICLES + 1;
    worldContainer.addChild(this.graphics);
  }

  update(
    grapple: ActiveGrapple | null,
    playerX: number,
    playerY: number,
    playerColor: number,
    dt: number,
  ): void {
    this.graphics.clear();
    this.glowGraphics.clear();

    if (grapple) {
      this.wasActive = true;
      this.fadeAlpha = 1;

      if (grapple.phase === 'traveling' && grapple.hook) {
        this.drawTravelLine(playerX, playerY, grapple.hook, playerColor);
      } else if (grapple.phase === 'retracting' && grapple.rope.length > 1) {
        // Calculate tension progress for pulse effect
        const currentLength = this.totalRopeLength(grapple.rope);
        const progress = 1 - Math.min(1, currentLength / (grapple.initialRopeLength || 1));
        const alpha = 0.6 + 0.4 * progress;

        this.drawSmoothRope(grapple.rope, playerColor, GRAPPLE_FX.ROPE_WIDTH, alpha);
        this.drawGlowRope(grapple.rope, playerColor, GRAPPLE_FX.ROPE_GLOW_WIDTH, alpha * 0.4);

        // Cache for fade
        this.lastParticles = grapple.rope.map(p => ({ x: p.x, y: p.y }));
        this.lastColor = playerColor;
      }
    } else if (this.wasActive && this.fadeAlpha > 0) {
      // Fade out after release
      this.fadeAlpha -= dt / GRAPPLE_FX.ROPE_FADE_DURATION;
      if (this.fadeAlpha > 0 && this.lastParticles.length > 1) {
        this.drawSmoothRopePoints(this.lastParticles, this.lastColor, GRAPPLE_FX.ROPE_WIDTH, this.fadeAlpha);
      }
      if (this.fadeAlpha <= 0) {
        this.fadeAlpha = 0;
        this.wasActive = false;
      }
    }
  }

  private drawTravelLine(px: number, py: number, hook: GrappleHook, color: number): void {
    this.graphics.moveTo(px, py);
    this.graphics.lineTo(hook.x, hook.y);
    this.graphics.stroke({ color, width: 2, alpha: 0.7 });
  }

  private drawSmoothRope(particles: RopeParticle[], color: number, width: number, alpha: number): void {
    const points = particles.map(p => ({ x: p.x, y: p.y }));
    this.drawSmoothRopePoints(points, color, width, alpha);
  }

  private drawSmoothRopePoints(points: { x: number; y: number }[], color: number, width: number, alpha: number): void {
    if (points.length < 2) return;

    this.graphics.moveTo(points[0].x, points[0].y);

    if (points.length === 2) {
      this.graphics.lineTo(points[1].x, points[1].y);
    } else {
      // Quadratic bezier through midpoints for smooth curve
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i];
        const p1 = points[i + 1];
        if (i < points.length - 2) {
          const midX = (p0.x + p1.x) * 0.5;
          const midY = (p0.y + p1.y) * 0.5;
          this.graphics.quadraticCurveTo(p0.x, p0.y, midX, midY);
        } else {
          this.graphics.lineTo(p1.x, p1.y);
        }
      }
    }

    this.graphics.stroke({ color, width, alpha });
  }

  private drawGlowRope(particles: RopeParticle[], color: number, width: number, alpha: number): void {
    if (particles.length < 2) return;

    this.glowGraphics.moveTo(particles[0].x, particles[0].y);

    if (particles.length === 2) {
      this.glowGraphics.lineTo(particles[1].x, particles[1].y);
    } else {
      for (let i = 0; i < particles.length - 1; i++) {
        const p0 = particles[i];
        const p1 = particles[i + 1];
        if (i < particles.length - 2) {
          const midX = (p0.x + p1.x) * 0.5;
          const midY = (p0.y + p1.y) * 0.5;
          this.glowGraphics.quadraticCurveTo(p0.x, p0.y, midX, midY);
        } else {
          this.glowGraphics.lineTo(p1.x, p1.y);
        }
      }
    }

    this.glowGraphics.stroke({ color, width, alpha });
  }

  private totalRopeLength(particles: RopeParticle[]): number {
    let length = 0;
    for (let i = 0; i < particles.length - 1; i++) {
      const dx = particles[i + 1].x - particles[i].x;
      const dy = particles[i + 1].y - particles[i].y;
      length += Math.sqrt(dx * dx + dy * dy);
    }
    return length;
  }

  destroy(): void {
    this.graphics.destroy();
    this.glowGraphics.destroy();
  }
}
